import React from 'react';
import classNames from 'classnames';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document } from '@contentful/rich-text-types';
import { Typography } from '@mui/material';

import CloudflareResizedImage from 'components/CloudflareResizedImage';

import Styles from './TravelEssentials.module.scss';

export interface TravelEssentialsProps {
  title: string;
  body: {
    json: Document;
    links: {
      assets: {
        block: {
          fileName: string;
          title: string;
          description: string;
          url: string;
          sys: {
            id: string;
          };
        }[];
      };
    };
  };
}

const renderHeading = (_: any, children: any) => (
  <Typography component="h3" variant="title3" mb={2} className={Styles.heading}>
    {children}
  </Typography>
);

const renderParagraph = (_: any, children: any) => (
  <Typography component="p" variant="body1" mb={4}>
    {children}
  </Typography>
);

export const TravelEssentials: React.FC<TravelEssentialsProps> = ({ title, body }) => {
  const assetBlockMap = new Map(body?.links?.assets?.block?.map((block) => [block.sys.id, block]));

  const RICHTEXT_OPTIONS = {
    renderNode: {
      [BLOCKS.HEADING_3]: renderHeading,
      [BLOCKS.PARAGRAPH]: renderParagraph,

      [BLOCKS.EMBEDDED_ASSET]: ({ data }: any) => {
        const image = assetBlockMap.get(data?.target?.sys?.id);
        if (!image) {
          return null;
        }
        return (
          <div className={Styles.imageContainer}>
            <CloudflareResizedImage
              layout="fill"
              src={image.url}
              alt={image.title}
              loading="lazy"
              className={Styles.image}
              sizes="(min-width: 769px) 45vw, (max-width: 768px) 100vw"
            />
          </div>
        );
      },
    },
  };

  if (!title || !body?.json) {
    return null;
  }

  return (
    <section
      className={classNames(Styles.travelEssentials, 'sl-section-container')}
      data-testid="travel-essentials"
      id="travel-essentials"
    >
      <Typography variant="title2" color="text.primary" mb={4}>
        {title}
      </Typography>
      <div className={Styles.body}>{documentToReactComponents(body.json, RICHTEXT_OPTIONS)}</div>
    </section>
  );
};

export default TravelEssentials;
